// src/pages/UserChat.jsx - FIXED EMOJI PICKER VERSION
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FaPaperPlane, FaTimes, FaComments, FaExclamationTriangle, FaSync, FaSearch, FaCalendar, FaRegClock, 
  FaPaperclip, FaSmile, FaEllipsisH, FaCopy, FaEdit, FaTrash, FaCheck, FaCircle, FaEye  } from 'react-icons/fa';
import { getFirebase, getProdDb, getCurrentUserEnv } from '@services/firebase';
import { getAppTimezone, formatChatTime } from './Settings';
import { onSnapshot, doc, collection, getDocs } from 'firebase/firestore';
import { readUserChat, addChatMessage, initializeUserChat, subscribeToUserChat, getUserRoleForChat, markChatAsRead, 
  writeUserChat, deleteMessage, updateMessage} from '@services/firebaseChat';
import ChatInsertModal from '../components/modals/ChatInsertModal';
import FileMessage from '../components/FileMessage';
import HomeProfileCardLarge from '../components/HomeProfileCardLarge';
import { useLocation } from 'react-router-dom';
import { useProfileActions } from '../hooks/useProfileActions';

const { auth, db } = getFirebase();

const UserChat = ({ userRole: propUserRole }) => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isSendingProfile, setIsSendingProfile] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [isRealTime, setIsRealTime] = useState(false);
  const [adminLiveColor, setAdminLiveColor] = useState('#aaa');
  const adminPresenceRef = useRef({});
  const appIsDownRef = useRef(false);
  const [receiverOnline, setReceiverOnline] = useState(null);
  const [receiverLastSeen, setReceiverLastSeen] = useState(null);
  const [userRole, setUserRole] = useState(() => {
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || '';
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return '';
    }
  });
  const [timeFilter, setTimeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showTimeFilters, setShowTimeFilters] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [messageMenuOpen, setMessageMenuOpen] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [copyToastVisible, setCopyToastVisible] = useState(false);
  const location = useLocation();
  const hasSentProfileImageRef = useRef(false);
  const hasSentStudioImageRef = useRef(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const { favourites, customProfiles, addToFavourites, addToCustom,isInFavourites,isInCustom}  = useProfileActions();
  const fromSupport = location.state?.from === 'support';

  const sendProfileImageMessage = useCallback(async (profileData) => {
    
    if (!user || !chat || !profileData || !profileData.image) {
      return;
    }

    try {
      setSending(true);
      setIsSendingProfile(true);
      setError(null);
      const userRole = await getUserRoleForChat(user.uid);

      // Create a descriptive message
      const description = [];
      if (profileData.theme) description.push(`Theme: ${profileData.theme}`);
      if (profileData.flavour) description.push(`Flavour: ${profileData.flavour}`);
      if (profileData.filling) description.push(`Filling: ${profileData.filling}`);
      if (profileData.cover) description.push(`Cover: ${profileData.cover}`);
      
      const messageText = `Profile Reference\n${description.join('\n')}`;

      const messageData = {
        text: messageText,
        senderId: user.uid,
        senderRole: userRole,
        receiverId: null,
        type: 'file',
        timestamp: new Date().toISOString(),
        fileData: {
          downloadUrl: profileData.image,
          name: `profile_${profileData.profileId || 'image'}.jpg`,
          size: 0,
          type: 'image/jpeg',
          profileData: profileData
        }
      };

      // For regular users, if they're starting a chat with support
      if (userRole !== 'Admin' && userRole !== 'Super') {
        messageData.receiverId = null;
      }
      await addChatMessage(user.uid, messageData);
      
      // Clear the stored profile data after sending
      sessionStorage.removeItem('chatProfileData');

    } catch (error) {
      console.error('? Error sending profile image to chat:', error);
      setError('Failed to send profile image: ' + error.message);
    } finally {
      setSending(false);
      setIsSendingProfile(false);
    }
  }, [user, chat]);

  // Update the useEffect with better debugging
  useEffect(() => {
    
    // Check if we have profile data stored from profile card
    const profileDataStr = sessionStorage.getItem('chatProfileData');
    
    if (profileDataStr && user && chat && !hasSentProfileImageRef.current) {
      try {
        const profileData = JSON.parse(profileDataStr);
        
        // Small delay to ensure chat is fully loaded
        const timer = setTimeout(() => {
          sendProfileImageMessage(profileData);
          hasSentProfileImageRef.current = true;
        }, 2000); // Increased delay to ensure everything is loaded
        
        return () => {
          clearTimeout(timer);
        };
      } catch (error) {
        console.error('? Error parsing profile data:', error);
        sessionStorage.removeItem('chatProfileData');
      }
    }
  }, [user, chat, sendProfileImageMessage]);

  // Send studio image shared from Image Studio
  useEffect(() => {
    const studioImageStr = sessionStorage.getItem('chatImageData');
    if (studioImageStr && user && !hasSentStudioImageRef.current) {
      try {
        const chatImageData = JSON.parse(studioImageStr);
        sessionStorage.removeItem('chatImageData');
        const timer = setTimeout(async () => {
          hasSentStudioImageRef.current = true;
          try {
            const role = await getUserRoleForChat(user.uid);
            await addChatMessage(user.uid, {
              text: 'Image Studio',
              senderId: user.uid,
              senderRole: role,
              receiverId: null,
              type: 'file',
              timestamp: new Date().toISOString(),
              fileData: {
                downloadUrl: chatImageData.url,
                name: chatImageData.fileName || 'studio-image.jpg',
                size: 0,
                mimeType: chatImageData.fileName?.endsWith('.png') ? 'image/png' : 'image/jpeg',
              },
            });
          } catch (err) {
            console.error('? Error sending studio image to chat:', err);
          }
        }, 2000);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('? Error parsing studio image data:', error);
        sessionStorage.removeItem('chatImageData');
      }
    }
  }, [user]);
  const ensureBookmarkedField = async (chatData) => {
    if (chatData.bookmarked === undefined) {
      const updatedChat = {
        ...chatData,
        bookmarked: true,
        updated: new Date().toISOString()
      };
      
      try {
        await writeUserChat(chatData.id, updatedChat);
        return updatedChat;
      } catch (error) {
        console.error('Error updating chat with bookmarked field:', error);
        return chatData;
      }
    }
    return chatData;
  };

  // Get user role and initialize chat
  useEffect(() => {
    const initializeChat = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Get user role
        const role = await getUserRoleForChat(user.uid);
        if (role) {
          setUserRole(role);
          localStorage.setItem('userRole', role);
        } else {
          // Keep the current role (from props or localStorage)
          console.warn('Fetched role is empty; using existing value');
        }

        // Initialize or load chat
        let userChat = await readUserChat(user.uid);

        if (!userChat) {
          userChat = await initializeUserChat(user.uid, role);
        } else if (!userChat.userRole) {
          userChat = await initializeUserChat(user.uid, role);
        }

        if (userChat.bookmarked === undefined) {
          const updatedChat = {
            ...userChat,
            bookmarked: true,
            updated: new Date().toISOString()
          };
          // Update the chat document with bookmarked field
          await writeUserChat(user.uid, updatedChat);
          setChat(updatedChat);
        } else {
          setChat(userChat);
        }

      } catch (error) {
        console.error('Error initializing chat:', error);
        setError('Failed to load chat');
      } finally {
        setLoading(false);
      }
    };

    initializeChat();
  }, [user]);

  // Setup real-time listener
  useEffect(() => {
    if (!user) return;

    const setupListener = () => {
      try {
        unsubscribeRef.current = subscribeToUserChat(user.uid, (updatedChat) => {
          if (updatedChat) {
             if (updatedChat.bookmarked === undefined) {
              // Update the chat with bookmarked field in background
              ensureBookmarkedField(updatedChat).then(chatWithBookmark => {
                setChat(chatWithBookmark);
              }).catch(error => {
                console.error('Error ensuring bookmarked field:', error);
                setChat(updatedChat); // Fallback to original chat
              });
            } else {
              setChat(updatedChat);
            }
            setIsRealTime(true);
            setConnectionStatus('online');
          } else {
            setIsRealTime(false);
          }
        });
      } catch (error) {
        console.error('Error setting up real-time listener:', error);
        setIsRealTime(false);
        setConnectionStatus('offline');
      }
    };

    setupListener();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user]);

  // Add this useEffect to mark messages as read when chat is opened
  useEffect(() => {
    const markAsRead = async () => {
      if (chat && user) {
        try {
          await markChatAsRead(chat.id, user.uid);
        } catch (error) {
          console.error('Failed to mark messages as read:', error);
        }
      }
    };

    markAsRead();
  }, [chat, user]);

  // Admin/Super live status indicator
  useEffect(() => {
    const recompute = () => {
      if (appIsDownRef.current) { setAdminLiveColor('#aaa'); return; }
      const now = Date.now();
      const entries = Object.values(adminPresenceRef.current);
      const active = entries.filter(e => e.isactive === true);
      const green = active.find(e => e.last_seen && now - new Date(e.last_seen).getTime() <= 10 * 60 * 1000);
      if (green) { setAdminLiveColor('#4CAF50'); return; }
      if (active.length > 0) { setAdminLiveColor('#FFC107'); return; }
      setAdminLiveColor('#aaa');
    };

    const prodDb = getProdDb();
    const unsubs = [];

    // Subscribe to appStatus
    unsubs.push(onSnapshot(doc(prodDb, 'siteConfig', 'appStatus'), (snap) => {
      appIsDownRef.current = snap.exists() ? !!snap.data().isDown : false;
      recompute();
    }, () => { appIsDownRef.current = false; recompute(); }));

    // Find all admin/super UIDs for the same env, then subscribe to each
    const currentEnv = getCurrentUserEnv();
    getDocs(collection(prodDb, 'cUsers')).then((snapshot) => {
      snapshot.forEach((docSnap) => {
        const d = docSnap.data();
        const docEnv = d.env || 'Prod';
        if ((d.isadminuser || d.issuperuser) && docEnv === currentEnv) {
          unsubs.push(onSnapshot(doc(prodDb, 'cUsers', docSnap.id), (snap) => {
            if (snap.exists()) {
              adminPresenceRef.current[snap.id] = { isactive: snap.data().isactive, last_seen: snap.data().last_seen };
            } else {
              delete adminPresenceRef.current[snap.id];
            }
            recompute();
          }));
        }
      });
    }).catch(() => {});

    return () => unsubs.forEach(u => u());
  }, []);

  // Subscribe to receiver's online presence
  useEffect(() => {
    if (!user || !chat?.messages) return;
    const messages = chat.messages;
    const receiverUid = [...messages].reverse().find(m => m.senderId && m.senderId !== user.uid)?.senderId || null;
    if (!receiverUid) {
      setReceiverOnline(null);
      return;
    }
    const unsub = onSnapshot(doc(getProdDb(), 'cUsers', receiverUid), (snap) => {
      if (!snap.exists()) { setReceiverOnline(false); setReceiverLastSeen(null); return; }
      const data = snap.data();
      const lastSeen = data?.last_seen;
      setReceiverLastSeen(lastSeen || null);
      if (!data?.isactive) { setReceiverOnline(false); return; }
      if (!lastSeen) { setReceiverOnline(false); return; }
      setReceiverOnline(Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000);
    });
    return () => unsub();
  }, [user, chat?.messages]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && 
          !event.target.closest('.emoji-picker-container') && 
          !event.target.closest('.emoji-btn')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
  const handleClickOutside = (event) => {
      if (messageMenuOpen && !event.target.closest('.message-options-menu') && !event.target.closest('.message-options-btn')) {
        setMessageMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [messageMenuOpen]);


  const handleProfileImageClick = useCallback((message) => {
    if (message.type === 'file' && message.fileData?.profileData) {
      setSelectedProfile(message.fileData.profileData);
      setShowProfileCard(true);
    }
  }, []);

  const handleCloseProfileCard = useCallback(() => {
    setShowProfileCard(false);
    setSelectedProfile(null);
  }, []);

// Handle message options click
  const handleMessageOptionsClick = (messageId, event) => {
    event.stopPropagation();
    const rect = (event.currentTarget).getBoundingClientRect();
    const top  = rect.bottom + window.scrollY;
    const left = rect.left   + window.scrollX;
    setMessageMenuOpen({
      messageId,
      position: { top, left }
    });
  };

  // Copy message to clipboard
  const handleCopyMessage = async (message) => {
    try {
      const textToCopy = message.type === 'file' ? message.fileData?.name || message.text : message.text;
      await navigator.clipboard.writeText(textToCopy);
      setMessageMenuOpen(null);
      setCopyToastVisible(true);
      setTimeout(() => setCopyToastVisible(false), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  // Start editing a message
  const handleEditMessage = (message) => {
    setEditMessageText(message.text);
    setEditingMessageId(message.id);
    setMessageMenuOpen(null);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditMessageText('');
  }; 

  // Save edited message
  const handleSaveEdit = async (message) => {
    if (!editMessageText.trim()) {
      handleCancelEdit();
      return;
    }
    try {
      await updateMessage(chat.id, message.id, { text: editMessageText.trim() });

      // ----- REFRESH THE CURRENT CHAT -----
      const newMessages = chat.messages.map((msg) =>
        msg.id === message.id
          ? { ...msg, text: editMessageText.trim(), isEdited: true }
          : msg
      );

      const updatedChat = {...chat, messages: newMessages};
      setChat(updatedChat);

      handleCancelEdit();

    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
  };

  // Delete message
  const handleDeleteMessage = async (message) => {
    try {
      await deleteMessage(chat.id, message.id);
      setDeleteConfirmId(null);
      setMessageMenuOpen(false);

      // ----- REFRESH THE CURRENT CHAT -----
      const refreshed = await readUserChat(chat.id);
      const updatedChat = { ...refreshed };
      setChat(updatedChat);

    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  // Check if message can be edited/deleted by user
  const canUserModifyMessage = (message) => {
    // User can only modify their own messages
    if (message.senderId !== user.uid) return false;
    
    return !message.read;
  };

  // Send message with proper receiver handling
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !user || !chat) return;

    try {
      setSending(true);
      setError(null);
      setShowEmojiPicker(false);

      const userRole = await getUserRoleForChat(user.uid);

      const messageData = {
        text: message.trim(),
        senderId: user.uid,
        senderRole: userRole,
        receiverId: null,
        type: 'text'
      };

      // For regular users, if they're starting a chat with support
      if (userRole !== 'Admin' && userRole !== 'Super') {
        messageData.receiverId = null;
      }

      await addChatMessage(user.uid, messageData);
      setMessage('');

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileInsert = async (fileData) => {
    if (!user || !chat) return;

    try {
      setSending(true);
      setError(null);

      const userRole = await getUserRoleForChat(user.uid);

      const messageData = {
        text: `${fileData.name}`,
        senderId: user.uid,
        senderRole: userRole,
        receiverId: null,
        type: 'file',
        timestamp: new Date().toISOString(),
        fileData: {
        downloadUrl: fileData.downloadUrl,
        name: fileData.name,
        size: fileData.size,
        type: fileData.type,
        mimeType: fileData.mimeType,
        bucketPath: fileData.bucketPath
      }
      };

      // For regular users, if they're starting a chat with support
      if (userRole !== 'Admin' && userRole !== 'Super') {
        messageData.receiverId = null;
      }

      await addChatMessage(user.uid, messageData);

    } catch (error) {
      console.error('Error sending file message:', error);
      setError('Failed to send file');
    } finally {
      setSending(false);
    }
  };

   // Handle file attachment
  const handleAttachmentClick = () => {
    setIsInsertModalOpen(true);
    setShowEmojiPicker(false);
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Get display name for message sender/receiver
  const getDisplayName = (message, isOwnMessage) => {
    if (isOwnMessage) return 'You';
    return 'Support';
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return [];

    const grouped = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateLabel = '';

      // Check if this message needs a new date header
      if (!currentGroup ||
        currentGroup.date.getDate() !== messageDate.getDate() ||
        currentGroup.date.getMonth() !== messageDate.getMonth() ||
        currentGroup.date.getFullYear() !== messageDate.getFullYear()) {

        // Format date label based on time filter
        if (timeFilter === 'lastWeek' || timeFilter === 'lastMonth' || timeFilter === 'lastYear') {
          // For filtered views, show full date
          dateLabel = messageDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } else {
          // For all time view, show relative dates
          if (messageDate.toDateString() === today.toDateString()) {
            dateLabel = 'Today';
          } else if (messageDate.toDateString() === yesterday.toDateString()) {
            dateLabel = 'Yesterday';
          } else {
            dateLabel = messageDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            });
          }
        }

        currentGroup = {
          date: messageDate,
          label: dateLabel,
          messages: []
        };
        grouped.push(currentGroup);
      }

      currentGroup.messages.push(message);
    });

    return grouped;
  };
  // Filter messages based on time filter and search
  const getFilteredMessages = () => {
    if (!chat?.messages) return [];

    let filtered = [...chat.messages];

    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const filterStartDate = new Date();
      const filterEndDate = new Date();

      switch (timeFilter) {
        case 'lastWeek':
          filterStartDate.setDate(now.getDate() - 14);
          filterEndDate.setDate(now.getDate() - 7);
          break;
        case 'lastMonth':
          filterStartDate.setMonth(now.getMonth() - 2);
          filterEndDate.setMonth(now.getMonth() - 1);
          break;
        case 'lastYear':
          filterStartDate.setFullYear(now.getFullYear() - 2);
          filterEndDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter(msg => {
        const msgDate = new Date(msg.timestamp);
        return msgDate >= filterStartDate && msgDate <= filterEndDate;
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(msg =>
        msg.text.toLowerCase().includes(query) ||
        new Date(msg.timestamp).toLocaleDateString().includes(query)
      );
    }

    return filtered;
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);

  const handleClose = () => {
    if (fromSupport) {
      navigate('/support');
    } else {
      navigate(-1);
    }
  };

  const retryConnection = () => {
    window.location.reload();
  };

  const filteredMessages = getFilteredMessages();

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="chat-page-container">
      <div className="chat-page-content">
        <div className="chat-header">
          {/* Calendar button in top left corner - no border */}
          <button
            className="calendar-filter-btn"
            onClick={() => setShowTimeFilters(!showTimeFilters)}
          >
            <FaCalendar />
          </button>

          {/* Chat title in center */}
          <div className="chat-title">
            <FaComments />
            Chat
            <span
              className="connection-status"
              style={{ color: adminLiveColor }}
              title="Live chat support status"
            >
              ● Live
            </span>
          </div>

          {/* Close button in top right */}
          <button className="chat-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>

          {/* Time filter dropdown */}
          {showTimeFilters && (
            <div className="time-filter-options">
              <button
                className={timeFilter === 'all' ? 'active' : ''}
                onClick={() => {
                  setTimeFilter('all');
                  setShowTimeFilters(false);
                }}
              >
                All Time
              </button>
              <button
                className={timeFilter === 'lastWeek' ? 'active' : ''}
                onClick={() => {
                  setTimeFilter('lastWeek');
                  setShowTimeFilters(false);
                }}
              >
                Last Week
              </button>
              <button
                className={timeFilter === 'lastMonth' ? 'active' : ''}
                onClick={() => {
                  setTimeFilter('lastMonth');
                  setShowTimeFilters(false);
                }}
              >
                Last Month
              </button>
              <button
                className={timeFilter === 'lastYear' ? 'active' : ''}
                onClick={() => {
                  setTimeFilter('lastYear');
                  setShowTimeFilters(false);
                }}
              >
                Last Year
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="chat-error">
            <FaExclamationTriangle />
            {error}
            <button onClick={retryConnection} className="retry-btn">
              <FaSync />
            </button>
          </div>
        )}

        {/* Search Section - Using the provided format */}
        <div className="search-section">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Phrase or Date (dd/mm/yyyy)"
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Filter Info */}
        {(timeFilter !== 'all' || searchTerm) && (
          <div className="filter-info">
            Showing {filteredMessages.length} of {chat?.messages?.length || 0} messages
            {(timeFilter !== 'all' || searchTerm) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setTimeFilter('all');
                  setSearchTerm('');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        <div className="chat-messages-container">
          {loading ? (
            <div className="empty-chat">
              <div className="loading-spinner"></div>
              Loading chat...
            </div>
          ) : !filteredMessages || filteredMessages.length === 0 ? (
            <div className="empty-chat">
              <div className="empty-chat-icon">
                <FaComments />
              </div>
              <p>No messages found</p>
              <small>
                {timeFilter !== 'all' || searchTerm ?
                  'Try changing your filters or search term' :
                  'Start a conversation with our support team'}
              </small>
            </div>
          ) : (
              (() => {
                const lastOwnMsg = [...filteredMessages].reverse().find(m => m.senderId === user.uid);
                const lastOwnMessageId = lastOwnMsg?.id || lastOwnMsg?.timestamp;
                return groupMessagesByDate(filteredMessages).map((group, groupIndex) => (
                <div key={groupIndex} className="date-group">
                  <div className="date-divider">
                    <span className="date-label">{group.label}</span>
                  </div>
                    {group.messages.map((msg) => {
                      const isOwnMessage = msg.senderId === user.uid;
                      const displayName = getDisplayName(msg, isOwnMessage);
                      const isEditing = editingMessageId === msg.id;
                      const canModify = canUserModifyMessage(msg);
                      const isLastOwn = isOwnMessage && (msg.id || msg.timestamp) === lastOwnMessageId;

                      return (
                        <div
                          key={msg.id || msg.timestamp}
                          className={`message ${isOwnMessage ? 'user-message' : 'support-message'}`}
                        >
                          <div className="message-bubble-row">
                          <div className="message-bubble">
                            {isEditing ? (
                              // Edit mode
                              <div className="edit-message-container">
                                <input
                                  type="text"
                                  value={editMessageText}
                                  onChange={(e) => setEditMessageText(e.target.value)}
                                  className="edit-message-input"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveEdit(msg);
                                    if (e.key === 'Escape') handleCancelEdit();
                                  }}
                                />
                                <div className="edit-actions">
                                  <button
                                    onClick={() => handleSaveEdit(msg)}
                                    className="save-edit-btn"
                                    disabled={!editMessageText.trim()}
                                  >
                                    <FaCheck />
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="cancel-edit-btn"
                                  >
                                    <FaTimes />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {msg.type === 'file' ? (
                                  <FileMessage
                                    chatId={chat?.id ?? ''}
                                    messageId={msg.id}
                                    signedUrl={msg.fileData?.downloadUrl ?? ''}
                                    mimeType={msg.fileData?.mimeType}
                                    fileName={msg.fileData?.name ?? 'Attachment'}
                                    fileSize={msg.fileData?.size}
                                    onClick={msg.fileData?.profileData ? () => handleProfileImageClick(msg) : undefined}
                                    isProfileImage={!!msg.fileData?.profileData}
                                  />
                                ) : (
                                  <>
                                    {msg.text}
                                  </>
                                )}
                              </>                            
                            )}
                          </div>
                          </div>
                          
                          <div className="message-meta">
                            {!isOwnMessage && <span className="message-sender">{displayName}</span>}
                            <span className="message-time">
                              <FaRegClock className="time-icon" />
                              {formatChatTime(msg.timestamp)}
                              {isOwnMessage && isLastOwn && (
                                msg.read
                                  ? <FaEye style={{ color: '#aaa', fontSize: 9, marginLeft: 4 }} title="Read" />
                                  : <FaCircle style={{ color: '#aaa', fontSize: 7, marginLeft: 4 }} title="Sent" />
                              )}
                            </span>
                            {/* Message options - Always show menu dot for all messages */}
                            {!isEditing && (
                              <button
                                className="message-options-btn"
                                onClick={(e) => handleMessageOptionsClick(msg.id, e)}
                                title="Message options"
                              >
                                <FaEllipsisH />
                              </button>
                            )}
                          </div>

                          {/* Message options menu - Show different options based on message type and read status */}
                          {messageMenuOpen && messageMenuOpen.messageId === msg.id && (
                            <div 
                              className="message-options-menu"
                              style={{
                                position: 'absolute',
                                 top:  messageMenuOpen.position?.top ?? 0,
                                left: messageMenuOpen.position?.left ?? 0,
                                transform: 'translateY(-100%)',
                                zIndex: 2000
                              }}
                            >
                              {/* Copy option - Always available for all messages */}
                              <button onClick={() => handleCopyMessage(msg)} className="menu-option" title="Copy">
                                <FaCopy className="menu-icon" />
                              </button>
                              
                              {/* Edit option - Only for user's own unread text messages */}
                              {isOwnMessage && canModify && msg.type === 'text' && (
                                <button onClick={() => handleEditMessage(msg)} className="menu-option" title="Edit">
                                  <FaEdit className="menu-icon" />
                                </button>
                              )}
                              
                              {/* Delete option - Only for user's own unread messages */}
                              {isOwnMessage && canModify && (
                                <button 
                                  onClick={() => {setDeleteConfirmId(msg.id); setMessageMenuOpen(null)}} 
                                  className="menu-option delete"
                                  title="Delete"
                                >
                                  <FaTrash className="menu-icon" />
                                </button>
                              )}

                              {/* For support messages OR user's read messages - Only show copy option */}
                              {(isOwnMessage && !canModify) && (
                                <div className="menu-disabled-options">
                                  <button className="menu-option disabled" title="Edit (disabled)" disabled>
                                    <FaEdit className="menu-icon disabled-icon" />
                                  </button>
                                  <button className="menu-option disabled" title="Delete (disabled)" disabled>
                                    <FaTrash className="menu-icon disabled-icon" />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Delete Confirmation */}
                          {deleteConfirmId === msg.id && (
                            <div className="delete-confirmation">
                              <div className="delete-actions">
                                <button onClick={() => handleDeleteMessage(msg)} className="confirm-btn" title="Confirm">
                                  <FaCheck />
                                </button>
                                <button onClick={() => setDeleteConfirmId(null)} className="cancel-btn" title="Cancel">
                                  <FaTimes/>
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Copy Toast */}
                          {copyToastVisible && (
                            <div className="copy-toast">
                              Copied to Clipboard
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ));
              })()
            )}
          <div ref={messagesEndRef} />
        </div>

        {/* Profile image sending progress bar */}
        {isSendingProfile && (
          <div className="profile-send-progress">
            <span>Sending cake profile to chat�</span>
            <div className="profile-send-bar">
              <div className="profile-send-bar-fill" />
            </div>
          </div>
        )}

        {/* Input area with attachment, message input, emoji, and send button in same line */}
        <form className="chat-input-form" onSubmit={sendMessage}>
          <div className="input-container">
            <button
              type="button"
              className="attachment-btn"
              onClick={handleAttachmentClick}
              disabled={sending || loading}
            >
              <FaPaperclip />
            </button>

            <button
              type="button"
              className="emoji-btn-inside"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={sending || loading}
            >
              <FaSmile />
            </button>

            <div className="input-with-emoji">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="chat-input"
                disabled={sending || loading}
              />
            </div>

            <button
              type="submit"
              className="chat-send-btn"
              disabled={!message.trim() || sending || loading}
            >
              <FaPaperPlane />
            </button>
          </div>

          {/* Emoji picker dropdown - FIXED POSITION */}
          {showEmojiPicker && (
            <div className="emoji-picker-container" ref={emojiPickerRef}>
              <div className="emoji-picker">
                <div className="emoji-grid">
                                                      {['\ud83d\udcde','\u274c','\ud83d\udcac','\ud83d\udce7','\ud83d\udc4b','\u2705',
                    '\u2764\ufe0f','\ud83d\udc93','\ud83d\udc96','\ud83d\udc9d','\ud83d\udc8c','\ud83e\udd70',
                    '\ud83c\udf89','\ud83c\udf82','\ud83d\udd6f\ufe0f','\ud83c\udf81','\ud83d\udc90','\ud83e\udd42',
                    '\ud83d\udc4d','\ud83d\udc4f','\u2b50','\ud83d\udd25','\ud83d\udcaf','\ud83e\udd1d',
                    '\ud83d\ude0a','\ud83d\ude18','\ud83e\udd24','\ud83d\ude0d','\ud83d\ude4f','\ud83e\udd14'].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      className="emoji-option"
                      onClick={() => handleEmojiSelect(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </form>
        <ChatInsertModal
          isOpen={isInsertModalOpen}
          onClose={() => setIsInsertModalOpen(false)}
          onFileInsert={handleFileInsert}
        />
        {showProfileCard && selectedProfile && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              position: 'relative',
              maxWidth: '400px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <HomeProfileCardLarge
                theme={selectedProfile.theme}
                flavour={selectedProfile.flavour}
                filling={selectedProfile.filling}
                cover={selectedProfile.cover}
                sponge={selectedProfile.sponge}
                cost={selectedProfile.cost}
                image={selectedProfile.image}
                cshape={selectedProfile.cshape}
                onClose={handleCloseProfileCard}
                onShare={() => {
                  // Implement share functionality if needed
                }}
                onAddToFavourites={async () => {
                  if (!selectedProfile) return;
                  
                  await addToFavourites({
                    id: selectedProfile.profileId,
                    theme: selectedProfile.theme,
                    cflavour: selectedProfile.flavour,
                    cfilling: selectedProfile.filling,
                    cover: selectedProfile.cover,
                    sponge: selectedProfile.sponge,
                    ccost: selectedProfile.cost,
                    cimage: selectedProfile.image,
                  });
                  
                }}
                onAddToCustom={async () => {
                  if (!selectedProfile) return;

                  await addToCustom({
                    id: selectedProfile.profileId,
                    theme: selectedProfile.theme,
                    cflavour: selectedProfile.flavour,
                    cfilling: selectedProfile.filling,
                    cover: selectedProfile.cover,
                    sponge: selectedProfile.sponge,
                    ccost: selectedProfile.cost,
                    cimage: selectedProfile.image,
                  });

                }}
                isInFavourites={selectedProfile ? isInFavourites(selectedProfile.profileId) : false}
                isInCustom={selectedProfile ? isInCustom(selectedProfile.profileId) : false}
                user={user}
                userRole={userRole}
                fromFavourites={false}
                profileId={selectedProfile.profileId}
                isVisible={true}
              />
            </div>
          </div>
        )}
      </div>

      <style>{`
        .chat-page-container {
          display: flex;
          flex-direction: column;
          padding: 5px;
          padding-bottom: 50px;
          background-color: #f5f5f5;
          width: 100%;
          box-sizing: border-box;
          min-height: 100vh;
        }

        .chat-page-content {
          position: relative;
          background: white;
          border-radius: 8px;
          width: 85vw;
          max-width: 550px;
          height: calc(100vh - 70px);
          padding: 15px;
          margin: 10px auto;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          box-sizing: border-box;
        }

        /* Chat Header - All in one row */
        .chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 15px;
          padding-bottom: 15px;
          border-bottom: 1px solid #f0f0f0;
          position: relative;
        }

        .calendar-filter-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          font-size: 16px;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .calendar-filter-btn:hover {
          color: #fa4848;
        }

        .chat-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 18px;
          font-weight: 600;
          color: #333;
          flex: 1;
          justify-content: center;
        }

        .chat-close-btn {
          background: none;
          border: none;
          font-size: 16px;
          cursor: pointer;
          color: #666;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-close-btn:hover {
          color: #333;
        }

        .connection-status {
          font-size: 12px;
        }

        .connection-status.online {
          color: #4CAF50;
        }

        .connection-status.offline {
          color: #ff4444;
        }

        /* Time Filter Options */
        .time-filter-options {
          position: absolute;
          top: 100%;
          left: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          z-index: 100;
          margin-top: 5px;
          min-width: 120px;
        }

        .time-filter-options button {
          display: block;
          width: 100%;
          padding: 10px 12px;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          border-bottom: 1px solid #f0f0f0;
        }

        .time-filter-options button:last-child {
          border-bottom: none;
        }

        .time-filter-options button:hover {
          background: #f5f5f5;
        }

        .time-filter-options button.active {
          background: #fa4848;
          color: white;
        }

        .date-group {
          margin-bottom: 10px;
        }

        .date-divider {
          display: flex;
          align-items: center;
          margin: 20px 0;
          text-align: center;
        }

        .date-divider::before,
        .date-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid #e0e0e0;
        }

        .date-label {
          padding: 0 15px;
          font-size: 12px;
          color: #666;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 0 10px;
        }

        .chat-error {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 12px;
          color: #ff4444;
          background: #ffeaea;
          padding: 5px 10px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .retry-btn {
          background: none;
          border: none;
          color: #ff4444;
          cursor: pointer;
          padding: 2px;
          border-radius: 3px;
        }

        /* Search Section - Using provided format */
        .search-section {
          margin-bottom: 15px;
        }

        .search-input-wrapper {
          flex: 1;
          margin: 0;
          background: #f0f2f5;
          border-radius: 8px;
          padding: 5px;
          display: flex;
          align-items: center;
          position: relative;
        }

        .search-icon {
          margin: 0 5px 0 5px;
          color: #666;
          font-size: 10px;
        }

        .search-input {
          flex: 1;
          padding: 4px 4px 4px 0;
          border: none;
          background: transparent;
          font-size: 10px;
          outline: none;
          color: #333;
        }

        .search-input::placeholder {
          color: #666;
          font-size: 10px;
        }

        .search-clear-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          font-size: 10px;
          padding: 4px;
          margin-left: 5px;
        }

        .filter-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #666;
          margin-bottom: 10px;
          padding: 5px 0;
        }

        .clear-filters-btn {
          background: none;
          border: none;
          color: #fa4848;
          cursor: pointer;
          font-size: 12px;
          text-decoration: underline;
        }

        .chat-messages-container {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 15px;
          scrollbar-width: none;
        }

        .chat-messages-container::-webkit-scrollbar {
          display: none;
        }

        /* UPDATED: Message styles to match AdminChat */
        .message {
          margin-bottom: 15px;
          max-width: 75%;
          overflow: visible;
        }

        .user-message {
          margin-left: auto;
        }

        .user-message .message-bubble {
          background: white;
          color: #333;
          padding: 10px 15px;
          border-radius: 18px 18px 5px 18px;
          border: 1px solid #e0e0e0;
          position: relative;
        }

        .support-message .message-bubble {
          background: #fde8e8;
          color: #333;
          padding: 10px 15px;
          border-radius: 18px 18px 18px 5px;
          border: 1px solid #f5c8c8;
        }

        .message-bubble-row {
          display: flex;
          align-items: flex-end;
          gap: 3px;
        }
        .message-bubble-row .message-bubble {
          flex: 1;
          min-width: 0;
        }
        .msg-status-icon {
          flex-shrink: 0;
          margin-bottom: 4px;
          line-height: 1;
          font-size: 11px;
        }
        .msg-status-read {
          color: #2196F3;
          font-size: 7px;
        }
        .msg-status-delivered {
          color: #4CAF50;
          font-size: 7px;
        }

        /* UPDATED: Message meta styles to match AdminChat */
        .message-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 4px;
          font-size: 11px;
          color: #888;
          padding: 0 5px;
        }

        .user-message .message-meta {
          flex-direction: row-reverse;
        }

        .message-sender {
          font-weight: 500;
        }

        .message-time {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .time-icon {
          font-size: 9px;
          opacity: 0.7;
        }

        /* Chat Input Form - All in one line */
        .chat-input-form {
          width: 100%;
          position: relative;
        }

        .input-container {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .attachment-btn {
          background: none;
          color: #666;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .attachment-btn:hover:not(:disabled) {
          color: #fa4848;
        }

        .attachment-btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        /* NEW: Input with emoji button inside */
        .input-with-emoji {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
        }

        .chat-input {
          flex: 1;
          padding: 12px 15px;
          border: 1px solid #ddd;
          border-radius: 25px;
          outline: none;
          font-size: 14px;
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }

        .chat-input:focus {
          border-color: #fa4848;
        }

        /* Emoji button beside attachment button */
        .emoji-btn-inside {
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .emoji-btn-inside:hover:not(:disabled) {
          color: #fa4848;
        }

        .emoji-btn-inside:disabled {
          color: #ccc;
          cursor: not-allowed;
        }

        /* FIXED: Emoji Picker Styles - Contained within window */
        .emoji-picker-container {
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          z-index: 100;
          margin-bottom: 10px;
          overflow: hidden;
        }

        .emoji-picker {
          width: 100%;
          padding: 10px;
          box-sizing: border-box;
        }

        .emoji-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 4px;
        }

        .emoji-option {
          background: none;
          border: none;
          font-size: 22px;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        .emoji-option:active {
          background: #f0f0f0;
          transform: scale(1.15);
        }

        .chat-send-btn {
          background: #fa4848;
          color: white;
          border: none;
          border-radius: 50%;
          width: 45px;
          height: 45px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .chat-send-btn:hover:not(:disabled) {
          background: #e03e3e;
        }

        .chat-send-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .empty-chat {
          text-align: center;
          color: #666;
          padding: 40px 20px;
        }

        .empty-chat-icon {
          font-size: 48px;
          color: #ddd;
          margin-bottom: 10px;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #fa4848;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 10px;
        }

        .message-bubble.file-message-bubble {
          background: transparent !important;
          padding: 0 !important;
          border-radius: 0 !important;
          border: none !important;
        }

        .user-message .message-bubble.file-message-bubble,
        .support-message .message-bubble.file-message-bubble {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }

        .file-message {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s;
          max-width: 300px;
          margin: 5px 0;
        }

        .file-message:hover {
          background: #e9ecef;
        }

        .image-preview {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .image-preview img {
          max-width: 100%;
          max-height: 200px;
          border-radius: 4px;
          object-fit: contain;
          display: block;
        }

        .image-fallback {
          text-align: center;
          font-size: 14px;
          padding: 20px;
          background: #e9ecef;
          border-radius: 4px;
          display: none;
        }

        .file-icon {
          font-size: 24px;
          text-align: center;
          margin-bottom: 8px;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .file-name {
          font-weight: 500;
          font-size: 14px;
          word-break: break-word;
        }

        .file-size {
          font-size: 12px;
          color: #666;
        }

        .user-message .file-message,
        .support-message .file-message {
          margin: 5px 0;
        }

        .file-link {
          color: #333;
          text-decoration: none;
          font-weight: 500;
        }

        .file-link:hover {
          text-decoration: underline;
          color: #fa4848;
        }

        .file-message {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s;
          max-width: 300px;
          margin: 5px 0;
        }

        .file-message:hover {
          background: #e9ecef;
        }

        .image-preview {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .image-preview img {
          max-width: 100%;
          max-height: 200px;
          border-radius: 4px;
          object-fit: contain;
          display: block;
        }

        .image-fallback {
          text-align: center;
          font-size: 14px;
          padding: 20px;
          background: #e9ecef;
          border-radius: 4px;
          display: none;
        }

        .file-info-only {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px;
        }

        .file-icon {
          font-size: 20px;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .file-name {
          font-weight: 500;
          font-size: 14px;
          word-break: break-word;
        }

        .file-size {
          font-size: 12px;
          color: #666;
        }

        /* Message options styles */
        .message-options-btn {
          background: transparent !important; 
          border: none !important;  
          color: #666; 
          cursor: pointer;
          padding: 2px !important;
          border-radius: 1px;
          opacity: 1; 
          transition: opacity 0.15s ease, color 0.15s ease;
        }

        .message:hover .message-options-btn {
          color: #fa4848;
        }

        .message-options-menu {
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,.15);
          padding: 4px;
          display: flex;
          gap: 4px;        
        }

        .menu-option {
          background: transparent !important;
          border: none !important;
          color: #666;
          cursor: pointer;
          padding: 4px !important;
          border-radius: 4px;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .menu-option:hover {
          background: #f5f5f5 !important;
          color: #fa4848;
        }

        .menu-option.delete:hover {
          background: #fff5f5 !important;
          color: #ff4444;
        }

        .copy-toast {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #666;
          color: #fff5f5;
          padding: 12px 20px;
          border-radius: 6px;
          font-size: 14px;
          z-index: 10000;
          animation: fadeInOut 2s ease-in-out;
        }

        .confirm-btn,
        .cancel-btn {
          background: transparent !important;
          border: none !important;         
          color: #666;
          cursor: pointer;
          padding: 6px 10px !important;
          border-radius: 4px;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .confirm-btn:hover {
          background: none !important; 
          color: #fa4848 !important;
        }
        .cancel-btn:hover {
          background: none !important;
          color: #333;
        }

        .delete-actions {
          display: flex;
          gap: 12px;
        }

        .edit-actions {
          display: flex;
          gap: 12px; 
        }

        .save-edit-btn,
        .cancel-edit-btn {
          background: transparent !important; 
          border: none !important; 
          color: #666; 
          cursor: pointer;
          padding: 6px 10px !important;
          border-radius: 4px;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .save-edit-btn:hover {
          background: none !important;
          color: #fa4848 !important;
        }
        .cancel-edit-btn:hover {
          background: none !important;
          color: #333;
        }

        @keyframes fadeInOut {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.8); }
          20%  { opacity: 1; transform: translate(-50%,-50%) scale(1);   }
          80%  { opacity: 1; transform: translate(-50%,-50%) scale(1);   }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(0.8); }
        }

        /* Edit message input */
        .edit-message-container {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .edit-message-input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          outline: none;
        }

        .edit-message-input:focus {
          border-color: #fa4848;
        }

        /* Disabled menu options */
        .menu-disabled-options {
          display: flex;
          gap: 4px;
        }

        .menu-option.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: transparent !important;
        }

        .menu-option.disabled:hover {
          background: transparent !important;
          color: #666 !important;
        }

        .disabled-icon {
          opacity: 0.4;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .profile-send-progress {
          padding: 6px 12px 4px;
          background: #fff8f0;
          border-top: 1px solid #fde8c8;
          font-size: 12px;
          color: #c0662a;
          font-weight: 500;
        }

        .profile-send-bar {
          height: 4px;
          background: #fde8c8;
          border-radius: 2px;
          overflow: hidden;
          margin-top: 4px;
        }

        .profile-send-bar-fill {
          height: 100%;
          width: 40%;
          background: #fa4848;
          border-radius: 2px;
          animation: profileSendSlide 1.2s ease-in-out infinite;
        }

        @keyframes profileSendSlide {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(-100%); }
        }

        @media (max-width: 450px) {
          .chat-page-content {
            width: 95vw;
            padding: 10px;
            margin: 5px auto;
            height: calc(100vh - 60px);
          }

          .message {
            max-width: 85%; /* Slightly wider on mobile */
          }

          .input-container {
            gap: 4px;
          }

          .attachment-btn {
            width: 38px;
            height: 38px;
          }

          .emoji-btn-inside {
            width: 28px;
            height: 28px;
            right: 8px;
          }

          .chat-send-btn {
            width: 42px;
            height: 42px;
          }

          .emoji-picker-container {
            left: 0;
            right: 0;
          }

          .emoji-grid {
            grid-template-columns: repeat(6, 1fr);
          }

          .chat-input {
            padding: 10px 40px 10px 12px;
          }
        }

        html[data-theme="dark"] .user-message .message-bubble {
          background: #3a3a3a !important;
          color: #ececec !important;
          border: 1px solid #4a4a4a !important;
        }

        html[data-theme="dark"] .support-message .message-bubble {
          background: rgba(250, 71, 71, 0.18) !important;
          color: #ececec !important;
          border: 1px solid rgba(250, 71, 71, 0.25) !important;
        }
      `}</style>
    </div>
  );
};

export default UserChat;